import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { SupabaseContext } from '../App';

function History() {
  const { supabase, session } = useContext(SupabaseContext);
  const [createdQuizzes, setCreatedQuizzes] = useState([]);
  const [participatedQuizzes, setParticipatedQuizzes] = useState([]);
  const [participants, setParticipants] = useState({});
  const navigate = useNavigate();

  const retryQuery = async (fn, maxRetries = 3, delay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const result = await fn();
        return result;
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        console.warn(`Retry ${i + 1}/${maxRetries} failed: ${error.message}`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  };

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const { data: created, error: createdError } = await retryQuery(() =>
          supabase.from('quizzes').select('code').eq('user_id', session.user.id)
        );
        if (createdError) throw new Error(`Created quizzes error: ${createdError.message}`);
        setCreatedQuizzes(created || []);

        const { data: participated, error: participatedError } = await retryQuery(() =>
          supabase.from('quiz_history').select('quiz_id, score, quizzes (code)').eq('user_id', session.user.id)
        );
        if (participatedError) throw new Error(`Participated quizzes error: ${participatedError.message}`);
        setParticipatedQuizzes(participated || []);
      } catch (error) {
        console.error('Fetch History Error:', error.message);
        alert('Failed to load history: ' + error.message);
      }
    };
    fetchHistory();
  }, [supabase, session]);

  const showParticipants = async (quizCode) => {
    try {
      const { data: quiz, error: quizError } = await retryQuery(() =>
        supabase.from('quizzes').select('id').eq('code', quizCode).single()
      );
      if (quizError) throw new Error(`Quiz fetch error: ${quizError.message}`);

      const { data: history, error: historyError } = await retryQuery(() =>
        supabase.from('quiz_history').select('user_id, score').eq('quiz_id', quiz.id)
      );
      if (historyError) throw new Error(`History fetch error: ${historyError.message}`);

      const participantData = await Promise.all(
        history.map(async (entry) => {
          const { data, error: userError } = await retryQuery(() =>
            supabase.rpc('get_user_email', { uid: entry.user_id })
          );
          if (userError) throw new Error(`User fetch error: ${userError.message}`);
          return {
            user_id: entry.user_id,
            email: data || 'Unknown',
            score: entry.score,
          };
        })
      );

      setParticipants((prev) => ({ ...prev, [quizCode]: participantData }));
    } catch (error) {
      console.error('Show Participants Error:', error.message);
      alert('Failed to fetch participants: ' + error.message);
    }
  };

  const editQuiz = (quizCode) => navigate(`/create?edit=${quizCode}`);

  if (!session) {
    navigate('/');
    return null;
  }

  return (
    <div className="page-container">
      <div className="auth-card">
        <h2>Your Quiz History</h2>
        <h3>Created Quizzes</h3>
        {createdQuizzes.length === 0 ? (
          <p>No quizzes created yet.</p>
        ) : (
          createdQuizzes.map((quiz) => (
            <div key={quiz.code} className="question-card">
              <p>Quiz Code: {quiz.code}</p>
              <div className="button-group">
                <button onClick={() => editQuiz(quiz.code)}>Edit Quiz</button>
                <button onClick={() => showParticipants(quiz.code)}>Show Participants</button>
              </div>
              {participants[quiz.code] && (
                <div>
                  <h4>Participants:</h4>
                  {participants[quiz.code].length === 0 ? (
                    <p>No participants yet.</p>
                  ) : (
                    participants[quiz.code].map((p, i) => (
                      <p key={i}>{p.email.split('@')[0]} - Score: {p.score}</p>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
        <h3>Participated Quizzes</h3>
        {participatedQuizzes.length === 0 ? (
          <p>You haven't participated in any quizzes yet.</p>
        ) : (
          participatedQuizzes.map((quiz, i) => (
            <div key={i} className="question-card">
              <p>Quiz Code: {quiz.quizzes.code} - Score: {quiz.score}</p>
            </div>
          ))
        )}
        <div className="button-group">
          <button onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    </div>
  );
}

export default History;